import React, { useState, useRef, useEffect } from 'react';
import { FinancialAccountWithBalances } from '../models/FinancialAccount';

interface AccountAutosuggestProps {
  accounts: FinancialAccountWithBalances[];
  value: number | null;
  onChange: (accountId: number | null) => void;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
}

export const AccountAutosuggest: React.FC<AccountAutosuggestProps> = ({
  accounts,
  value,
  onChange,
  placeholder = 'Search accounts...',
  label,
  disabled = false,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Get selected account to display its name
  const selectedAccount = accounts.find(acc => acc.id === value);

  // Filter accounts based on search term
  const filteredAccounts = accounts.filter(account => {
    const searchLower = searchTerm.toLowerCase();
    const accountName = (account.display_name || account.account_type || '').toLowerCase();
    const accountType = account.account_type.toLowerCase();
    const reference = account.reference?.toLowerCase() || '';
    const accountId = String(account.id);

    return accountName.includes(searchLower) ||
           accountType.includes(searchLower) ||
           reference.includes(searchLower) ||
           accountId.includes(searchLower);
  });

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlighted index when filtered accounts change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [searchTerm]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleSelectAccount = (accountId: number) => {
    onChange(accountId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = () => {
    onChange(null);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev =>
          prev < filteredAccounts.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => prev > 0 ? prev - 1 : 0);
        break;
      case 'Enter':
        e.preventDefault();
        if (filteredAccounts[highlightedIndex]) {
          handleSelectAccount(filteredAccounts[highlightedIndex].id);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setSearchTerm('');
        break;
    }
  };

  const getAccountDisplayName = (account: FinancialAccountWithBalances) => {
    const accountName = account.display_name || getAccountTypeLabel(account.account_type);
    const parts = [accountName];
    if (account.reference && !account.display_name) {
      parts.push(`#${account.reference}`);
    }
    return parts.join(' ');
  };

  const getAccountTypeLabel = (accountType: string) => {
    const labels: { [key: string]: string } = {
      'platform': 'Platform',
      'payment_gateway': 'Payment Gateway',
      'management_company': 'Management Company',
      'association': 'Association',
      'vendor': 'Vendor',
      'customer': 'Customer',
    };
    return labels[accountType] || accountType;
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}

      <div className="relative">
        <input
          type="text"
          value={isOpen ? searchTerm : (selectedAccount ? getAccountDisplayName(selectedAccount) : '')}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary focus:border-primary disabled:bg-gray-100 disabled:cursor-not-allowed"
        />

        {value && !disabled && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {isOpen && filteredAccounts.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto">
          {filteredAccounts.map((account, index) => (
            <button
              key={account.id}
              type="button"
              onClick={() => handleSelectAccount(account.id)}
              className={`w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none ${
                index === highlightedIndex ? 'bg-gray-100' : ''
              }`}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-medium text-gray-900">
                    {getAccountDisplayName(account)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {getAccountTypeLabel(account.account_type)}
                    {account.balance !== undefined && (
                      <span className="ml-2">
                        • Balance: ${account.balance.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {isOpen && filteredAccounts.length === 0 && searchTerm && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3 text-center text-gray-500">
          No accounts found
        </div>
      )}
    </div>
  );
};
